import React, { useEffect, useState } from 'react';
import { Calendar, Users, ClipboardList, CheckSquare, Pill, Activity, Bell, ArrowRight, Plus, TrendingUp } from 'lucide-react';
import DashboardLayout from '../../components/common/DashboardLayout';
import { StatCard, Card, LoadingState, StatusBadge, Avatar, DoshaTag, Badge, Button, JoinGMeetButton } from '../../components/common/UI';
import { useAuth } from '../../context/AuthContext';
import { appointmentsAPI, usersAPI, attendanceAPI, prescriptionsAPI } from '../../services/api';
import toast from 'react-hot-toast';

const QUICK_ACTIONS = [
  { href: '/doctor/prescriptions', label: 'New Prescription', icon: Plus, color: 'bg-blue-50 text-blue-600' },
  { href: '/doctor/appointments', label: 'Appointments', icon: Calendar, color: 'bg-emerald-50 text-emerald-600' },
  { href: '/doctor/patients', label: 'My Patients', icon: Users, color: 'bg-amber-50 text-amber-600' },
  { href: '/doctor/attendance', label: 'Attendance', icon: CheckSquare, color: 'bg-teal-50 text-teal-600' },
];

const NAV_ITEMS = [
  { path: '/doctor/dashboard', label: 'Dashboard', icon: Activity },
  { path: '/doctor/appointments', label: 'Appointments', icon: Calendar },
  { path: '/doctor/patients', label: 'My Patients', icon: Users },
  { path: '/doctor/attendance', label: 'Attendance', icon: CheckSquare },
  { path: '/doctor/prescriptions', label: 'Prescriptions', icon: Pill },
];

const fmt = (d) => new Date(d).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });

const getGreeting = () => {
  const hour = new Date().getHours();
  if (hour < 12) return 'Good Morning';
  if (hour < 17) return 'Good Afternoon';
  return 'Good Evening';
};

export default function DoctorDashboard() {
  const { user } = useAuth();
  const [upcoming, setUpcoming] = useState([]);
  const [patients, setPatients] = useState([]);
  const [todayAttendance, setTodayAttendance] = useState(null);
  const [monthlyAttendance, setMonthlyAttendance] = useState([]);
  const [recentPrescriptions, setRecentPrescriptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [markingAttendance, setMarkingAttendance] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const [apptRes, patientsRes, attRes, presRes] = await Promise.all([
          appointmentsAPI.getUpcoming(),
          usersAPI.getDoctorPatients(),
          attendanceAPI.getAll({ month: new Date().getMonth() + 1, year: new Date().getFullYear() }),
          prescriptionsAPI.getAll()
        ]);
        setUpcoming(apptRes.data.appointments || []);
        setPatients(patientsRes.data.patients || []);
        const monthAtt = attRes.data.attendance || [];
        setMonthlyAttendance(monthAtt);
        const today = new Date().toDateString();
        const todayAtt = monthAtt.find(a => new Date(a.date).toDateString() === today);
        setTodayAttendance(todayAtt);
        setRecentPrescriptions((presRes.data.prescriptions || []).slice(0, 3));
      } catch (err) {
        toast.error('Failed to load dashboard');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  // Attendance logic left exactly as-is, per request
  const handleMarkAttendance = async (status) => {
    setMarkingAttendance(true);
    try {
      const now = new Date();
      const timeStr = `${now.getHours().toString().padStart(2,'0')}:${now.getMinutes().toString().padStart(2,'0')}`;
      const res = await attendanceAPI.mark({
        date: now.toISOString(),
        status,
        checkIn: status === 'present' ? timeStr : undefined
      });
      setTodayAttendance(res.data.attendance);
      toast.success(`Marked as ${status}`);
    } catch { toast.error('Failed to mark attendance'); }
    finally { setMarkingAttendance(false); }
  };

  if (loading) return <DashboardLayout navItems={NAV_ITEMS} role="doctor"><LoadingState /></DashboardLayout>;

  const profile = user?.doctorProfile || {};
  const firstName = user?.name?.split(' ').slice(0, 2).join(' ');
  const todayCount = upcoming.filter(a => new Date(a.date).toDateString() === new Date().toDateString()).length;

  const presentDays = monthlyAttendance.filter(a => a.status === 'present').length;
  const absentDays = monthlyAttendance.filter(a => a.status === 'absent').length;
  const markedDays = monthlyAttendance.length;
  const attendanceRate = markedDays > 0 ? Math.round((presentDays / markedDays) * 100) : null;

  return (
    <DashboardLayout navItems={NAV_ITEMS} role="doctor">
      {/* Header */}
      <div className="flex items-start justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">{getGreeting()}, Dr. {firstName}</h1>
          <p className="text-gray-500 text-sm mt-1">
            {profile.specialization && <span>{profile.specialization} • </span>}
            {new Date().toLocaleDateString('en-IN', { weekday: 'long', month: 'long', day: 'numeric' })}
          </p>
        </div>
        <a
          href="/doctor/notifications"
          className="w-10 h-10 rounded-xl bg-white border border-gray-200 flex items-center justify-center text-gray-500 hover:text-blue-600 hover:border-blue-200 transition-colors flex-shrink-0"
          aria-label="Notifications"
        >
          <Bell className="w-4.5 h-4.5" />
        </a>
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        {QUICK_ACTIONS.map((action) => (
          <a
            key={action.href}
            href={action.href}
            className="flex items-center gap-3 p-4 bg-white border border-gray-100 rounded-xl hover:border-blue-200 hover:shadow-sm transition-all"
          >
            <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${action.color}`}>
              <action.icon className="w-4 h-4" />
            </div>
            <span className="text-sm font-medium text-gray-700">{action.label}</span>
          </a>
        ))}
      </div>

      {/* Attendance quick mark — logic unchanged */}
      <div className={`rounded-2xl p-5 mb-6 border ${todayAttendance ? 'bg-emerald-50 border-emerald-200' : 'bg-amber-50 border-amber-200'}`}>
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <p className="font-semibold text-gray-800">Today's Attendance</p>
            {todayAttendance ? (
              <p className="text-sm text-gray-600 mt-0.5">
                Status: <span className="font-bold text-emerald-700 capitalize">{todayAttendance.status}</span>
                {todayAttendance.checkIn && ` • Check-in: ${todayAttendance.checkIn}`}
              </p>
            ) : <p className="text-sm text-amber-700 mt-0.5">Not yet marked for today</p>}
          </div>
          {!todayAttendance && (
            <div className="flex gap-2">
              <Button onClick={() => handleMarkAttendance('present')} loading={markingAttendance} size="sm" variant="primary">Present</Button>
              <Button onClick={() => handleMarkAttendance('absent')} loading={markingAttendance} size="sm" variant="danger">Absent</Button>
            </div>
          )}
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <StatCard title="My Patients" value={patients.length} subtitle="Active patients" icon={Users} color="blue" />
        <StatCard title="Today's Sessions" value={todayCount} subtitle="Scheduled today" icon={Calendar} color="emerald" />
        <StatCard title="This Week" value={upcoming.length} subtitle="Upcoming sessions" icon={ClipboardList} color="amber" />
        <StatCard title="Experience" value={`${profile.experience || 0}yr`} subtitle={profile.qualification || 'Not set'} icon={Activity} color="teal" />
      </div>

      {/* Monthly attendance summary */}
      {markedDays > 0 && (
        <Card
          title="This Month's Attendance"
          subtitle={new Date().toLocaleDateString('en-IN', { month: 'long', year: 'numeric' })}
          className="mb-6"
        >
          <div className="flex items-center gap-8 flex-wrap">
            <div className="flex items-center gap-2">
              <TrendingUp className="w-4 h-4 text-emerald-500" />
              <p className="text-2xl font-bold text-gray-800">{attendanceRate}%</p>
              <span className="text-xs text-gray-400">attendance rate</span>
            </div>
            <div>
              <p className="text-lg font-semibold text-emerald-600">{presentDays}</p>
              <p className="text-xs text-gray-500">Days present</p>
            </div>
            <div>
              <p className="text-lg font-semibold text-red-500">{absentDays}</p>
              <p className="text-xs text-gray-500">Days absent</p>
            </div>
            <div>
              <p className="text-lg font-semibold text-gray-700">{markedDays}</p>
              <p className="text-xs text-gray-500">Days marked</p>
            </div>
          </div>
        </Card>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Upcoming Appointments */}
        <div className="lg:col-span-2">
          <Card
            title="Today's Schedule"
            subtitle="Upcoming patient sessions"
            action={
              <a href="/doctor/appointments" className="text-xs font-medium text-blue-600 hover:text-blue-700 flex items-center gap-1">
                View all <ArrowRight className="w-3 h-3" />
              </a>
            }
          >
            {upcoming.length === 0 ? (
              <div className="text-center py-10">
                <Calendar className="w-8 h-8 text-gray-300 mx-auto mb-2" />
                <p className="text-gray-400 text-sm">No upcoming appointments</p>
              </div>
            ) : (
              <div className="space-y-3">
                {upcoming.map(a => (
                  <div key={a._id} className="flex items-center gap-4 p-4 border border-gray-100 rounded-xl hover:border-blue-200 hover:bg-blue-50/30 transition-all">
                    <Avatar name={a.patient?.name} color="blue" size="md" />
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-gray-800 text-sm">{a.patient?.name}</p>
                      <p className="text-gray-500 text-xs">{a.therapy?.name || a.type} • {fmt(a.date)} at {a.startTime}</p>
                      {a.patient?.patientProfile?.dosha && <DoshaTag dosha={a.patient.patientProfile.dosha} />}
                    </div>
                    <div className="flex items-center gap-2">
                      <StatusBadge status={a.status} />
                      <JoinGMeetButton appointment={a} />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Card>
        </div>

        {/* Patient list */}
        <div>
          <Card
            title="My Patients"
            subtitle={`${patients.length} assigned`}
            action={
              patients.length > 6 && (
                <a href="/doctor/patients" className="text-xs font-medium text-blue-600 hover:text-blue-700 flex items-center gap-1">
                  View all <ArrowRight className="w-3 h-3" />
                </a>
              )
            }
          >
            {patients.length === 0 ? (
              <div className="text-center py-10">
                <Users className="w-8 h-8 text-gray-300 mx-auto mb-2" />
                <p className="text-gray-400 text-sm">No patients assigned yet</p>
              </div>
            ) : (
              <div className="space-y-3">
                {patients.slice(0, 6).map(p => (
                  <div key={p._id} className="flex items-center gap-3 p-3 hover:bg-gray-50 rounded-xl transition-colors">
                    <Avatar name={p.name} color="blue" size="sm" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-800 truncate">{p.name}</p>
                      <div className="flex items-center gap-1 mt-0.5">
                        {p.patientProfile?.dosha && <DoshaTag dosha={p.patientProfile.dosha} />}
                      </div>
                    </div>
                    <div className="w-12 text-right">
                      <p className="text-xs text-gray-500">{p.patientProfile?.therapyProgress || 0}%</p>
                      <div className="w-full bg-gray-100 rounded-full h-1.5 mt-1">
                        <div className="h-1.5 rounded-full bg-blue-500" style={{ width: `${p.patientProfile?.therapyProgress || 0}%` }} />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Card>
        </div>
      </div>

      {/* Recent Prescriptions */}
      <div className="mt-6">
        <Card
          title="Recent Prescriptions"
          subtitle="Last issued to your patients"
          action={
            <a href="/doctor/prescriptions" className="text-xs font-medium text-blue-600 hover:text-blue-700 flex items-center gap-1">
              View all <ArrowRight className="w-3 h-3" />
            </a>
          }
        >
          {recentPrescriptions.length === 0 ? (
            <div className="text-center py-10">
              <Pill className="w-8 h-8 text-gray-300 mx-auto mb-2" />
              <p className="text-gray-400 text-sm">No prescriptions issued yet</p>
            </div>
          ) : (
            <div className="space-y-2">
              {recentPrescriptions.map((p) => (
                <div key={p._id} className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl">
                  <Avatar name={p.patient?.name} color="blue" size="sm" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-800 truncate">{p.patient?.name}</p>
                    <p className="text-xs text-gray-500">
                      {p.medicines?.length || 0} medicine{p.medicines?.length === 1 ? '' : 's'} • {fmt(p.createdAt)}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>
    </DashboardLayout>
  );
}